import { h, Fragment } from 'preact'
import { useState, useEffect, useRef } from 'preact/hooks'
import Container from '@components/container'
import ImageLoad from '@components/image-load'
import { ArrowRight } from '@components/icons'
import { ButtonLabel, ButtonText } from '@components/button'
import classnames from 'classnames'
import { dateFormat, timeFormat } from '@util/date'
import { withHydration } from '@util/hydrate'
import { imageProps } from '@data/formats'
import { json } from '@util/request'
import { isClient } from '@util/env'
import { useQueryString } from '@util/useQueryString'


import './style.less'


function leftOrRight(x, starts = 0) {
	return Math.ceil((x + starts)/2) % 2 === 0
}


function FiltersUrl({ includeArticleTypes = [], includeEvents = true }) {
	const [query, updateQuery] = useQueryString('show')
	return <Filters 
		includeArticleTypes={includeArticleTypes}
		includeEvents={includeEvents}
		select={query} 
		updateSelected={selected => updateQuery(selected)} 
	/>
}


export const FiltersUrlLive = withHydration(FiltersUrl, 'FiltersUrlLive')


function Filters({ select = null, includeArticleTypes = [], includeEvents = true, updateSelected = () => {} }) {

	const [selected, setSelected] = useState(select)

	const set = name => {
		setSelected(selected === name ? null : name)
		updateSelected(selected === name ? null : name)
	}

	const numFilters = [includeEvents, ...includeArticleTypes].filter(Boolean).length
	if (numFilters < 2) return

	return <div class="article-grid__tags">
		{includeEvents && <>
			<ButtonLabel 
				rounded 
				class="margin-top-1" 
				onClick={() => set('event')} 
				active={selected === 'event'}>
				Events
			</ButtonLabel>
			{' '}
		</>} 
		{includeArticleTypes.map(type => <>
			<ButtonLabel 
				rounded 
				class="margin-top-1" 
				onClick={() => set(type.slug)} 
				active={selected === type.slug}>
				{type.name}
			</ButtonLabel>
			{' '}	
		</>)}
	</div>
}


export default function ArticleGrid(object) {

	const { 
		title = '', 
		subtitle = '', 
		content = '', 
		initialItems = [],
		link,
		limit = 0,
		useUrl = false,
		includeEvents = true,
		includeArticleTypes = [],
	} = object

	const includeArticleTypeSlugs = includeArticleTypes.map(type => type.slug)
	const [query, updateQuery] = useQueryString('show')
	const [articles, setArticles] = useState(filterItems(initialItems, useUrl ? query : null))
	const [loading, setLoading] = useState(false)
	const hasIntro = (title || subtitle || content)
	const position = index => hasIntro ? leftOrRight(index, 1) : !leftOrRight(index, 2)	

	function filterItems(items, selected = null) {
		let output = items

		if (!includeEvents) {
			output = output.filter(item => item.type !== 'event')
		}
		if (includeArticleTypes.length) {
			output = output.filter(item => {
				if (item.type !== 'article') return true 
				let inArticleTypes = false
				for (const filter of item.filters) {
					if (includeArticleTypeSlugs.includes(filter)) {
						inArticleTypes = true
						continue;
					}
				}
				return inArticleTypes
			})
		}
		if (selected) {
			output = output.filter(item => item.filters.includes(selected))
		}
		output = limit ? output.slice(0, limit) : output
		return output
	}

	const onChangeFilter = selected => {
		if (!isClient) return
		setLoading(true)
		json('/api/articles-events.json', json => {
			if (!json.length) return false
			const articles = filterItems(json, selected)
			setArticles(articles)
			setLoading(false)
		})
	}

	const firstUpdate = useRef(true)
	useEffect(() => {
		if (!firstUpdate.current) {
			onChangeFilter(useUrl ? query : null)
		}
		firstUpdate.current = false
	}, [query])

	return (
		<section class="margin-bottom">
			<ul class={classnames('article-grid', loading && 'article-grid--loading')}>
				{ hasIntro && 
					<li class="article-grid-item article-grid-item--head article-grid-item--big text--fade-p">
						<div class="article-grid-item--head__content">
							{title && <h2 class="text--x-large margin-top-0">{title}</h2>}
							{subtitle && <p>{subtitle}</p>}
							{content && <div dangerouslySetInnerHTML={{ __html: content }}></div>}
							<Filters 
								includeEvents={includeEvents} 
								includeArticleTypes={includeArticleTypes}
								updateSelected={onChangeFilter} 
							/>
						</div>
					</li>
				}

				{(limit ? articles.slice(0, limit) : articles).map((article, index) => {
					
					const image = imageProps(article.acf)

					const isEvent = (article.type === 'event')

					const isFutureEvent = (article) => {
						if (!isClient || article.type !== 'event') return false
						const dateNow = new Date()
						const dateISO = new Date(new Date(article.acf.date_start || article.date).toDateString())
						if (dateISO >= dateNow) return true
					}

					const classes = classnames(
						'article-grid-item',
						position(index) ? 'article-grid-item--big' : 'article-grid-item--small',
						!image && 'article-grid-item--text-only',
						isEvent && !isFutureEvent(article) ? 'article-grid-item--past' : ''
					)

					const date = isEvent ? dateFormat(article.acf.date_start) : dateFormat(article.date)
					const time = isEvent && timeFormat(article.acf.time_start) || ''
					const dateTime = time ? `${date} — ${time}` : date

					const eventPrefix = (article) => {
						if (!isClient) return ''
						if (article.type !== 'event') return ''
						if (isFutureEvent(article)) return 'Upcoming: '
						return 'Past event: '
					}

					const sub = isEvent 
						? dateTime && `${eventPrefix(article)}${dateTime}` || ''
						: `Posted on ${date}`

					return <li class={classes} key={article.id}>
						<a href={article.link}>
							{image && <div class="article-grid-item__image">
								<ImageLoad class="object-fit object-fit--fill" {...image} sizes="(min-width:608px) 60vw, 100vw" />
							</div>}
							<div class="article-grid-item__content">
								<div class="first-last">
									<h3 class="article-grid-item__title margin-0">{article.title_raw}</h3>
									
									<p class="article-grid-item__text margin-bottom-0 text--xx-small">
										{article.filterNames.map(name => <span class="article-grid-item__button">{name}</span>)}
										<span dangerouslySetInnerHTML={{ __html : sub.replace(/\s/g, '&nbsp;') }} /> 
									</p>
									{!image && <p class="article-grid-item__excerpt" dangerouslySetInnerHTML={{ __html : article.excerpt_raw }}></p>}

								</div>
								<div class="article-grid-item__content__arrow"><ArrowRight/></div>
							</div>
						</a>
					</li>
				})}
			</ul>
			{link && <div class="text--large align-center">
				<ButtonText rounded class="margin-top-4 margin-bottom-4" {...link}>{link.title}</ButtonText>
			</div>}
		</section>
	)
}


export const ArticleGridLive = withHydration(ArticleGrid, 'ArticleGridLive')